import React from 'react';
import useMetaTags from "../hooks/useMetaTags";
import {
  RotateCcw,
  XCircle,
  ShieldOff,
  AlertTriangle,
  Wallet,
  Truck,
  Banknote,
  ClipboardList,
  RefreshCw,
  Mail,
} from 'lucide-react';

const LAST_UPDATED = "July 18, 2026";

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-10">
    <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-800 mb-4">
      <span className="p-2 bg-[#679729]/10 rounded-lg text-[#679729]">
        <Icon size={20} />
      </span>
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3 pl-1">
      {children}
    </div>
  </div>
);

const RefundPolicy = () => {
  useMetaTags({
    title: "Refund Policy | Organic Pads",
    description: "OrganicPads' Refund Policy — order cancellation, our hygiene-product return rules, and how refunds are handled for damaged, defective, or wrong items.",
    keywords: "organic pads refund policy, return policy, damaged product refund, hygiene product returns",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/refund-policy",
    ogTitle: "Refund Policy | Organic Pads",
    ogDescription: "How order cancellations, returns, and refunds work at OrganicPads.",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/refund-policy",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Refund Policy | Organic Pads",
    twitterDescription: "How order cancellations, returns, and refunds work at OrganicPads.",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Refund Policy",
    "url": "https://organicpads.shop/refund-policy",
    "description": "OrganicPads' Refund Policy detailing cancellation, non-returnable hygiene product rules, and refund terms for damaged, defective, or wrong items.",
    "publisher": { "@type": "Organization", "name": "OrganicPads", "url": "https://organicpads.shop" },
    "dateModified": "2026-07-18",
  };

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 mt-10 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#679729] mb-4">
            <RotateCcw size={24} />
            <span className="text-lg font-medium">Orders, Returns & Refunds</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Refund Policy</h1>
          <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <p className="text-gray-700 leading-relaxed mb-10">
          This Refund Policy explains how order cancellations, returns, and refunds work when you buy products from
          OrganicPads, the business that owns and operates the Organic Pads brand and the website organicpads.shop.
          By placing an order with us, you agree to the terms below.
        </p>

        <Section icon={XCircle} title="1. Order Cancellation">
          <p>
            You may cancel an order free of charge any time before it has been dispatched, by contacting us with
            your order ID. Once an order has been shipped, it can no longer be cancelled and the terms below for
            returns will apply instead.
          </p>
        </Section>

        <Section icon={ShieldOff} title="2. Non-Returnable Hygiene Products">
          <p>
            Because our products are sanitary/hygiene products, once a package has been delivered and opened or
            used, it cannot be returned or exchanged for hygiene and safety reasons. We are unable to accept returns
            on the basis of a simple change of mind.
          </p>
        </Section>

        <Section icon={AlertTriangle} title="3. Exception: Damaged, Defective, or Wrong Item Received">
          <p>
            If you receive a product that is damaged, defective, or different from what you ordered, we will make it
            right. To be eligible:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>You must report the issue to us within 48–72 hours of delivery.</li>
            <li>You must share clear photos or an unboxing video showing the damage, defect, or incorrect item, along with your order ID.</li>
          </ul>
          <p>Once verified, we will offer a replacement or a refund, at your preference.</p>
        </Section>

        <Section icon={Wallet} title="4. Refund Method & Timeline">
          <p>
            Approved refunds are issued to your original payment method and are typically processed within 7–10
            business days of approval, though your bank or payment provider may take a few additional days to
            reflect the credit.
          </p>
        </Section>

        <Section icon={Truck} title="5. Shipping Charges">
          <p>
            Shipping charges are non-refundable, except in cases where the return is due to our error — such as a
            damaged, defective, or wrong item — in which case any shipping charges you paid will also be refunded.
          </p>
        </Section>

        <Section icon={Banknote} title="6. Cash on Delivery (COD) Orders">
          <p>
            For orders paid via Cash on Delivery, approved refunds are issued via bank transfer or UPI. We will ask
            you to share your bank account or UPI details securely so we can process the refund.
          </p>
        </Section>

        <Section icon={ClipboardList} title="7. How to Request a Refund or Report an Issue">
          <p>To request a cancellation, refund, or report a damaged/defective/wrong item, please contact us with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your order ID</li>
            <li>A brief description of the issue</li>
            <li>Photos or a video, where applicable</li>
          </ul>
          <p>You can reach us via email, phone, or WhatsApp using the details below.</p>
        </Section>

        <Section icon={RefreshCw} title="8. Changes to This Policy">
          <p>
            We may update this Refund Policy from time to time. Any changes will be posted on this page with a
            revised "Last updated" date.
          </p>
        </Section>

        <Section icon={Mail} title="9. Contact Us">
          <ul className="list-none space-y-1">
            <li>Email: <a href="mailto:organicpads.shop@gmail.com" className="text-[#679729] hover:underline">organicpads.shop@gmail.com</a></li>
            <li>Phone: <a href="tel:+917203802493" className="text-[#679729] hover:underline">+91 720 3802 493</a></li>
            <li>Address: Nikol, Ahmedabad, Gujarat 382350</li>
          </ul>
        </Section>
      </div>
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default RefundPolicy;
